import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, Theme } from '../../context/ThemeContext';
import { UX_THEME_V5_ENABLED } from '../../config/themeAccess';
import { IconMoon, IconSun, IconTheme } from '../../icons';

/**
 * ThemePreferenceSection — AccountPage-Sektion "Darstellung" (M-D5 Welle 3
 * W3-1, `plan-ux-d5-w3-light-flip.md` §2/§4).
 *
 * Self-gated (Outer/Inner-Gate-Muster, Bestandskonvention — vgl.
 * `InboundActivation`/`ApiAccessSection`): rendert `null`, solange
 * {@link UX_THEME_V5_ENABLED} `false` ist. `AccountPage` bindet diese
 * Komponente daher unconditional ein.
 *
 * UI-Muster (Radio-Karten) 1:1 aus `EmailPreferencesSection` in
 * `AccountPage.tsx` übernommen (gleiche Datei, konsistentes Look&Feel).
 *
 * @module components/account/ThemePreferenceSection
 */
const ThemePreferenceSection: React.FC = () => {
  const { t } = useTranslation('account');

  if (!UX_THEME_V5_ENABLED) {
    return null;
  }

  return <ThemePreferenceSectionInner t={t} />;
};

/**
 * Innerer Teil, ausgelagert damit `useTheme()` erst NACH dem Flag-Gate
 * gerufen wird — `useTheme()` wirft außerhalb des `ThemeProvider` fail-closed
 * (s. `ThemeContext.tsx`), ein bedingter Hook-Aufruf im äußeren Rumpf würde
 * gegen die Rules-of-Hooks verstoßen. Diese innere Komponente wird nur
 * gemountet, wenn das Flag bereits an ist.
 */
const ThemePreferenceSectionInner: React.FC<{ t: (key: string, opts?: Record<string, unknown>) => string }> = ({ t }) => {
  const { theme, setTheme } = useTheme();

  const options: { value: Theme; label: string; description: string; icon: React.ReactNode }[] = [
    {
      value: 'dark',
      label: t('theme.options.dark.label', { defaultValue: 'Dunkel' }),
      description: t('theme.options.dark.description', { defaultValue: 'Dunkle Oberfläche (Standard).' }),
      icon: <IconMoon className="text-secondary" />,
    },
    {
      value: 'light',
      label: t('theme.options.light.label', { defaultValue: 'Hell' }),
      description: t('theme.options.light.description', { defaultValue: 'Helle Oberfläche.' }),
      icon: <IconSun className="text-secondary" />,
    },
  ];

  return (
    <div className="mt-8 bg-surface-raised shadow-lg rounded-xl border border-hairline p-6" data-testid="theme-preference-section">
      <h2 className="text-xl font-semibold mb-4 text-primary flex items-center">
        <IconTheme className="mr-2 text-accent-ink" size={20} />
        {t('theme.title', { defaultValue: 'Darstellung' })}
      </h2>
      <p className="text-muted text-sm mb-6">
        {t('theme.description', { defaultValue: 'Wähle, wie FaeBill für dich aussehen soll.' })}
      </p>

      <div className="space-y-3" role="radiogroup" aria-label={t('theme.title', { defaultValue: 'Darstellung' })}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={theme === option.value}
            onClick={() => setTheme(option.value)}
            className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
              theme === option.value
                ? 'border-indigo-500 bg-[var(--tone-accent-bg)]'
                : 'border-hairline bg-[rgb(var(--surface-inset-rgb)/0.3)] hover:border-strong hover:bg-[rgb(var(--surface-inset-rgb)/0.5)]'
            } cursor-pointer`}
          >
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-4 bg-surface-raised">
                {option.icon}
              </div>
              <div className="text-left">
                <p className="text-primary font-medium">{option.label}</p>
                <p className="text-muted text-sm">{option.description}</p>
              </div>
            </div>
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                theme === option.value ? 'border-indigo-500 bg-indigo-500' : 'border-strong'
              }`}
            >
              {theme === option.value && (
                <svg className="w-3 h-3 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ThemePreferenceSection;
